import { EventBus } from '@pp/toolkit';

import { DictionaryFetcher } from './DictionaryFetcher';
import { DictionaryValueFormatter } from './DictionaryValueFormatter';
import type { Dictionary, DictionaryServiceOptions, DynamicValues } from './types';

export class DictionaryService<File> extends EventBus<{
  ready: () => void;
  update: () => void;
  locale: () => void;
  'fetch-failed': () => void;
  'no-translation': (key: string) => void;
}> {
  private options?: DictionaryServiceOptions;
  private _defaultDictionary: Dictionary | undefined;
  private _dictionary: Dictionary | undefined;
  private _locale?: string;
  private files?: Set<string> = new Set();
  private abortPreviousFetch?: () => void;

  private _isFetching = false;
  private _isReady = false;
  private _hasFailed = false;

  get dictionary(): Dictionary | undefined {
    return this._dictionary;
  }

  private set dictionary(value: Dictionary | undefined) {
    this._dictionary = Object.assign({}, this.defaultDictionary, value);
    this.dispatchEvent('update');
  }

  /**
   * Not meant to be used in runtime, for testing purposes only
   *
   * @internal
   * */
  get defaultDictionary(): Dictionary | undefined {
    return this._defaultDictionary;
  }

  /**
   * Not meant to be used in runtime, for testing purposes only
   *
   * @internal
   * */
  set defaultDictionary(value: Dictionary | undefined) {
    this._defaultDictionary = value;
    this.dictionary = Object.assign({}, this.dictionary, value);
  }

  get locale(): string | undefined {
    return this._locale;
  }

  private set locale(value: string | undefined) {
    this._locale = value;
    this.dispatchEvent('locale');
  }

  get isFetching(): boolean {
    return this._isFetching;
  }

  private set isFetching(value: boolean) {
    this._isFetching = value;
  }

  get isReady(): boolean {
    return this._isReady;
  }

  private set isReady(value: boolean) {
    this._isReady = value;
    if (this._isReady) this.dispatchEvent('ready');
  }

  get hasFailed(): boolean {
    return this._hasFailed;
  }

  private set hasFailed(value: boolean) {
    this._hasFailed = value;
    if (value) this.dispatchEvent('fetch-failed');
  }

  constructor(
    private dictionaryFetcher: DictionaryFetcher<File>,
    private dictionaryValueFormatter: DictionaryValueFormatter
  ) {
    super();
  }

  update(locale: string, files: string[], options?: DictionaryServiceOptions): Promise<void> {
    this.options = options;

    const localeChanged = this.locale !== locale;
    const newFiles: Set<string> = new Set(files.filter((file) => !this.files?.has(file)));
    let filesToFetch: Set<string>;
    // If locale stayed the same
    if (!localeChanged) {
      // If there are no new files to fetch
      if (newFiles.size === 0) {
        // There is no need to fetch anything
        return Promise.resolve();
      } else {
        // if there are new files though - fetch them
        filesToFetch = newFiles;
      }
    } else {
      // If locale changed - fetch all files
      filesToFetch = new Set(files);
    }
    this.isFetching = true;
    this.locale = locale;
    this.abortPreviousFetch?.();
    const { fetchingPromise, abort } = this.dictionaryFetcher.fetch(locale, [...filesToFetch]);
    this.abortPreviousFetch = abort;
    return fetchingPromise
      .then((dictionary) => {
        this.abortPreviousFetch = undefined;
        this.isFetching = false;
        this.hasFailed = false;
        this.files = new Set(filesToFetch);
        // if locale changed, there is no need to merge dictionaries
        this.dictionary = localeChanged ? dictionary : Object.assign({}, this.dictionary, dictionary);
        if (!this.isReady) {
          this.isReady = true;
        }
      })
      .catch(() => {
        this.isFetching = false;
        this.hasFailed = true;
      });
  }

  getValueByKey(key: string, dynamicValues?: DynamicValues): string | null | undefined {
    if (!this.dictionary || !this.locale) return;

    const keyToUse = this.options?.keyMap?.[key] ?? key;

    const value = this.dictionary[keyToUse];
    if (!value) {
      this.dispatchEvent('no-translation', keyToUse);
      return null;
    }

    return this.dictionaryValueFormatter.format(value, this.locale, dynamicValues);
  }

  getSafeValueByKey(key: string, dynamicValues?: DynamicValues): string {
    const rawValue = this.getValueByKey(key, dynamicValues);
    if (rawValue === undefined) {
      return '';
    }
    if (rawValue === null) {
      return key;
    }
    return rawValue;
  }
}
